import { useElementSize } from '@lib/hooks/sizing/element-size';
import classNames from 'classnames';
import Hls from 'hls.js';
import { observer } from 'mobx-react';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import css from './MomentMergeVideoPlayer.module.scss';

// Import extracted components and hooks
import { CenterControls } from './components/CenterControls';
import { VideoControls } from './components/VideoControls';
import { VideoWrapper } from './components/VideoWrapper';
import { useCleanup } from './hooks/useCleanup';
import { useFullscreenEvents } from './hooks/useFullscreenEvents';
import { useKeyboardEvents } from './hooks/useKeyboardEvents';
import { useMemoizedCalculations } from './hooks/useMemoizedCalculations';
import { useProgressBar } from './hooks/useProgressBar';
import { useScrubTo } from './hooks/useScrubTo';
import { useTimeInput } from './hooks/useTimeInput';
import { useTransitionMonitoring } from './hooks/useTransitionMonitoring';
import { useVideoControls } from './hooks/useVideoControls';
import { useVideoEventHandlers } from './hooks/useVideoEventHandlers';
import { useVideoLoadingHandlers } from './hooks/useVideoLoadingHandlers';
import { useVideoPlayerState } from './hooks/useVideoPlayerState';
import { useVideoSetup } from './hooks/useVideoSetup';
import { useVideoTransitionLogic } from './hooks/useVideoTransitionLogic';
import { MomentMergeVideoPlayerProps, MomentMergeVideoPlayerRef } from './types';

export const MomentMergeVideoPlayer = observer(
  forwardRef<MomentMergeVideoPlayerRef, MomentMergeVideoPlayerProps>(
    (props: MomentMergeVideoPlayerProps, ref) => {
      const {
        onReady,
        hidden,
        containerClassName,
        activeMomentId,
        clipStore,
        playbackSource = 'preview',
        onPlaybackSourceChange,
        playbackSourceLabels,
        onOriginalUnavailable,
        chapters,
        commentMarks,
        selectedCommentId,
        onCommentMarkClick,
        selection,
        onSelectionChange,
        onSelectionDragChange,
      } = props;

      // Use custom hooks for state management
      const {
        store,
        preloadingVideoRef,
        video1ReadyRef,
        video2ReadyRef,
        lastVirtualTimestampRef,
        pauseStartTimeRef,
        previousClipIdsRef,
        currentClipIndex,
        setCurrentClipIndex,
        activeVideo,
        setActiveVideo,
        video1Clip,
        setVideo1Clip,
        video2Clip,
        setVideo2Clip,
        video1Loading,
        setVideo1Loading,
        video2Loading,
        setVideo2Loading,
        video1TargetTime,
        setVideo1TargetTime,
        video2TargetTime,
        setVideo2TargetTime,
        showHoverTime,
        setShowHoverTime,
        hoverTime,
        setHoverTime,
        hoverPosition,
        setHoverPosition,
        timeInputValue,
        setTimeInputValue,
        showSkipFeedback,
        setShowSkipFeedback,
        resetVideoState,
        setupVideoClips,
        showSkipFeedbackTemporarily,
      } = useVideoPlayerState(clipStore);

      // Refs
      const video1Ref = useRef<HTMLVideoElement>(null);
      const video2Ref = useRef<HTMLVideoElement>(null);
      const hls1Ref = useRef<Hls | null>(null);
      const hls2Ref = useRef<Hls | null>(null);
      const [containerRef, containerWidth] = useElementSize();
      const progressBarHitboxRef = useRef<HTMLDivElement>(null);

      // Track video dimensions for proper container sizing
      const [videoDimensions, setVideoDimensions] = useState<{
        width: number;
        height: number;
      } | null>(null);

      // Use memoized calculations hook
      const { isActiveVideoLoading, progressPercentage } = useMemoizedCalculations({
        store,
        activeVideo,
        video1Loading,
        video2Loading,
        video1Clip,
        video2Clip,
      });

      // Use scrubTo hook
      const { scrubTo, scrubToWithKeyboardBounds } = useScrubTo({
        store,
        currentClipIndex,
        video1Clip,
        video2Clip,
        activeVideo,
        video1Ref,
        video2Ref,
        setCurrentClipIndex,
        setActiveVideo,
        setVideo1Clip,
        setVideo2Clip,
        setVideo1TargetTime,
        setVideo2TargetTime,
        lastVirtualTimestampRef,
      });

      // Use video controls hook
      const {
        timeInputRef,
        togglePlayPause,
        toggleFullscreen,
        toggleMute,
        handleVolumeChange,
        handleTimeInputSubmit: handleTimeInputSubmitFromHook,
        formatTime,
        formatRemainingTime,
        getVolumeIcon,
      } = useVideoControls({
        video1Ref,
        video2Ref,
        activeVideo,
        scrubTo,
        store,
      });

      // Use video transition logic hook
      const { handleClipTransition, handleTimeUpdate } = useVideoTransitionLogic({
        store,
        currentClipIndex,
        activeVideo,
        video1Ref,
        video2Ref,
        preloadingVideoRef,
        video1ReadyRef,
        video2ReadyRef,
        setCurrentClipIndex,
        setActiveVideo,
        setVideo1Clip,
        setVideo2Clip,
        setVideo1TargetTime,
        setVideo2TargetTime,
        lastVirtualTimestampRef,
      });

      // Use video event handlers hook
      const { handleVideo1TimeUpdate, handleVideo2TimeUpdate, handleVideoEnded } =
        useVideoEventHandlers({
          handleTimeUpdate,
          video1Ref,
          video2Ref,
          video1Clip,
          video2Clip,
          onVideoEnded: () => {
            if (currentClipIndex < 0 || currentClipIndex >= store.clips.length) {
              console.warn(
                `Invalid currentClipIndex in handleVideoEnded: ${currentClipIndex}, clips length: ${store.clips.length}`,
              );
              store.setIsPlaying(false);
              preloadingVideoRef.current = null;
              pauseStartTimeRef.current = null;
              return;
            }

            if (currentClipIndex >= store.clips.length - 1) {
              store.setIsPlaying(false);
              preloadingVideoRef.current = null;
              pauseStartTimeRef.current = null;
            }
          },
        });

      // Use video loading handlers hook
      const { handleVideo1LoadingChange, handleVideo2LoadingChange } = useVideoLoadingHandlers({
        setVideo1Loading,
        setVideo2Loading,
      });

      // Use progress bar hook
      const {
        handlePointerDown,
        handlePointerMove,
        handlePointerUp,
        handlePointerCancel,
        handleProgressLeave,
        handleProgressEnter,
      } = useProgressBar({
        totalDuration: store.totalClipsDuration,
        scrubTo,
        setHoverTime,
        setHoverPosition,
        setShowHoverTime,
        progressBarHitboxRef,
        onSelectionChange,
        selection,
        onSelectionDragChange,
      });

      // Use time input hook
      const {
        handleTimeInputChange,
        handleTimeInputFocus,
        handleTimeInputBlur,
        handleTimeInputSubmit,
      } = useTimeInput({
        timeInputValue,
        setTimeInputValue,
        formatTime,
        currentTime: store.currentTime,
        handleTimeInputSubmitFromHook,
      });

      // Calculate and update video dimensions when videos load
      useEffect(() => {
        const updateDimensionsFromVideo = (videoElement: HTMLVideoElement) => {
          if (videoElement.videoWidth && videoElement.videoHeight) {
            setVideoDimensions({
              width: videoElement.videoWidth,
              height: videoElement.videoHeight,
            });
          }
        };

        // Set up listeners for both videos
        const video1 = video1Ref.current;
        const video2 = video2Ref.current;

        const handleVideo1Metadata = () => {
          if (video1) updateDimensionsFromVideo(video1);
        };
        const handleVideo2Metadata = () => {
          if (video2) updateDimensionsFromVideo(video2);
        };

        if (video1) {
          if (video1.videoWidth && video1.videoHeight) {
            updateDimensionsFromVideo(video1);
          } else {
            video1.addEventListener('loadedmetadata', handleVideo1Metadata);
          }
        }

        if (video2) {
          if (video2.videoWidth && video2.videoHeight) {
            updateDimensionsFromVideo(video2);
          } else {
            video2.addEventListener('loadedmetadata', handleVideo2Metadata);
          }
        }

        return () => {
          if (video1) video1.removeEventListener('loadedmetadata', handleVideo1Metadata);
          if (video2) video2.removeEventListener('loadedmetadata', handleVideo2Metadata);
        };
      }, [activeVideo]);

      // Expose ref methods
      useImperativeHandle(ref, () => ({
        scrubTo,
        video: activeVideo === 'video1' ? video1Ref.current : video2Ref.current,
        pause: () => {
          // Pause the active video element
          const activeVideoRef = activeVideo === 'video1' ? video1Ref : video2Ref;
          const video = activeVideoRef.current;
          if (video) {
            video.pause();
          }
          store.setIsPlaying(false);
        },
      }));

      // Use hooks for effects
      useFullscreenEvents({ store });
      useKeyboardEvents({
        scrubToWithKeyboardBounds,
        togglePlayPause,
        showSkipFeedbackTemporarily,
        currentTime: store.currentTime,
      });

      // Use video setup hook
      useVideoSetup({
        store,
        currentClipIndex,
        setCurrentClipIndex,
        setActiveVideo,
        resetVideoState,
        setupVideoClips,
        lastVirtualTimestampRef,
        previousClipIdsRef,
      });

      // Use transition monitoring hook
      useTransitionMonitoring({
        store,
        currentClipIndex,
        activeVideo,
        video1Ref,
        video2Ref,
        preloadingVideoRef,
        handleClipTransition,
      });

      // Use cleanup hook
      useCleanup({ store });

      return (
        <div
          className={classNames(css.videoContainer, css.aspectRatioContainer, containerClassName)}
          ref={containerRef}
          data-video-container
          style={{
            display: hidden ? 'none' : 'block',
            aspectRatio: videoDimensions
              ? `${videoDimensions.width}/${videoDimensions.height}`
              : '16/9',
          }}
        >
          <div
            className={`${css.videoWrapper} ${store.isFullscreen ? css.fullscreen : ''}`}
            onClick={togglePlayPause}
          >
            <VideoWrapper
              video1Ref={video1Ref}
              video2Ref={video2Ref}
              hls1Ref={hls1Ref}
              hls2Ref={hls2Ref}
              activeVideo={activeVideo}
              video1Clip={video1Clip}
              video2Clip={video2Clip}
              currentClipIndex={currentClipIndex}
              video1Loading={video1Loading}
              video2Loading={video2Loading}
              video1TargetTime={video1TargetTime}
              video2TargetTime={video2TargetTime}
              store={store}
              activeMomentId={activeMomentId}
              onVideo1TimeUpdate={handleVideo1TimeUpdate}
              onVideo2TimeUpdate={handleVideo2TimeUpdate}
              onVideoEnded={handleVideoEnded}
              onVideo1Ready={() => {
                video1ReadyRef.current = true;
                onReady?.();
              }}
              onVideo2Ready={() => {
                video2ReadyRef.current = true;
                onReady?.();
              }}
              onVideo1LoadingChange={handleVideo1LoadingChange}
              onVideo2LoadingChange={handleVideo2LoadingChange}
              isPlaying={store.isPlaying}
              playbackSource={playbackSource}
              onOriginalUnavailable={onOriginalUnavailable}
            />

            {/* Control overlay positioned over the entire video container */}
            <div className={css.controlsOverlay}>
              <CenterControls
                isPlaying={store.isPlaying}
                isActiveVideoLoading={isActiveVideoLoading}
                showSkipFeedback={showSkipFeedback}
                onTogglePlayPause={(e) => {
                  e.stopPropagation();
                  togglePlayPause();
                }}
              />

              <VideoControls
                isPlaying={store.isPlaying}
                volume={store.volume}
                isMuted={store.isMuted}
                isFullscreen={store.isFullscreen}
                currentTime={store.currentTime}
                totalDuration={store.totalClipsDuration}
                timeInputValue={timeInputValue}
                timeInputRef={timeInputRef}
                progressPercentage={progressPercentage}
                showHoverTime={showHoverTime}
                hoverTime={hoverTime}
                hoverPosition={hoverPosition}
                progressBarHitboxRef={progressBarHitboxRef}
                onTogglePlayPause={togglePlayPause}
                onToggleMute={toggleMute}
                onVolumeChange={handleVolumeChange}
                onToggleFullscreen={toggleFullscreen}
                onTimeInputChange={handleTimeInputChange}
                onTimeInputSubmit={handleTimeInputSubmit}
                onTimeInputFocus={handleTimeInputFocus}
                onTimeInputBlur={handleTimeInputBlur}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerCancel}
                onProgressLeave={handleProgressLeave}
                onProgressEnter={handleProgressEnter}
                playbackSource={playbackSource}
                onPlaybackSourceChange={onPlaybackSourceChange}
                playbackSourceLabels={playbackSourceLabels}
                containerWidth={containerWidth}
                formatTime={formatTime}
                formatRemainingTime={formatRemainingTime}
                getVolumeIcon={getVolumeIcon}
                chapters={chapters}
                commentMarks={commentMarks}
                selectedCommentId={selectedCommentId}
                onCommentMarkClick={onCommentMarkClick}
                selection={selection}
                selectable={!!onSelectionChange}
              />
            </div>
          </div>
        </div>
      );
    },
  ),
);
